



































































import Vue from 'vue';
import { cloneDeep, has, kebabCase } from 'lodash';
import { BButton, BCol, BFormRow, BFormRadio, BFormRadioGroup } from 'bootstrap-vue';

import ZenMultiselect from '@reciprocity/multiselect';
import GenericCard from '@/components/cards/GenericCard.vue';

import { buildURL } from './mapper_utils';
import { ObjectType, ObjectItem, Filter } from './mapper_types';
import { getRequest } from '../../utils/client/client';

interface ObjectList {
  [type: string]: Array<ObjectItem>;
}

interface LoadingMap {
  [type: string]: Array<Boolean>;
}

interface CancelTokenModel {
  type: string;
  cancelFn: Function;
}

const CANCEL_MESSAGE: string = 'canceled';

export default Vue.extend({
  name: 'MapperFilterModal',
  components: {
    GenericCard,
    BButton,
    ZenMultiselect,
    BFormRow,
    BCol,
    BFormRadio,
    BFormRadioGroup,
  },
  props: {
    mapTypes: {
      type: Object,
      required: true,
    },
    pageObjectType: {
      type: String,
      required: true,
    },
    selectedType: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array as () => Filter[],
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    const typeList: Array<ObjectType> = this.mapTypes[this.pageObjectType];
    const objectList: ObjectList = {};
    const loadingObjects: LoadingMap = {};
    Object.keys(this.mapTypes).forEach(typeName => {
      loadingObjects[typeName] = [];
      objectList[typeName] = [];
    });
    return {
      selected: <ObjectType>this.selectedType,
      typeList: <Array<ObjectType>>typeList,
      relevantFilters: <Filter[]>cloneDeep(this.filters),
      objectList: <ObjectList>objectList,
      loadingObjects,
      cancelFetch: <Array<CancelTokenModel>>[],
    };
  },
  computed: {
    typesOptions() {
      return this.mapTypes[this.selected.model] || [];
    },
  },
  watch: {
    selected(): void {
      this.relevantFilters = [
        {
          object: null,
          type: null,
        },
      ];
    },
  },
  created: function() {
    this.relevantFilters.forEach((filter, idx) => this.fetchObjectsForType(idx, filter.type));
  },
  methods: {
    isLoading(type: ObjectType | null): Boolean {
      return !!(type && type.model && this.loadingObjects[type.model] && this.loadingObjects[type.model].length);
    },
    confirm(): void {
      const payload = [this.selected, this.relevantFilters];
      if (this.$store) {
        this.$store.dispatch('modals/confirm', payload);
      } else {
        window.GGRC.zenModal.$emit('changeType', ...payload);
        this.close();
      }
    },
    close() {
      this.$emit('close');
    },
    cancel(): void {
      this.$nextTick(() => {
        // Do data reset after the modal hides
        this.selected = this.selectedType;
        this.relevantFilters = cloneDeep(this.filters);
      });
    },
    changeType(idx: number, value: ObjectType | null): void {
      this.relevantFilters[idx].type = value;
      this.relevantFilters[idx].object = null;
      this.fetchObjectsForType(idx, value);
    },
    changeObject(idx: number, value: ObjectItem): void {
      this.relevantFilters[idx].object = value;
    },
    addRule(): void {
      this.relevantFilters.push({
        object: null,
        type: null,
      });
    },
    removeRule(idx: number): void {
      this.cancelRequest(idx);
      if (this.relevantFilters.length > 1) {
        // remove filter
        Object.keys(this.loadingObjects).forEach(type => {
          this.loadingObjects[type].splice(idx, 1);
        });
        this.relevantFilters.splice(idx, 1);
        return;
      }

      // clear first filter
      this.resetFilters();
    },
    resetFilters() {
      Object.keys(this.loadingObjects).forEach(type => {
        this.loadingObjects[type] = [];
      });
      this.cancelFetch = [];
      this.relevantFilters = [
        {
          object: null,
          type: null,
        },
      ];
    },
    cancelRequest(idx: number) {
      // cancel request only if there isn't any row waiting for the already called request results
      if (this.cancelFetch[idx]) {
        const loadingType = this.cancelFetch[idx].type;
        const anotherRowWaiting = this.loadingObjects[loadingType].some((loadingVal, row) => loadingVal && row !== idx);

        if (!anotherRowWaiting) {
          this.cancelFetch[idx].cancelFn(CANCEL_MESSAGE);
        }
      }
    },
    fetchObjectsForType(idx: number, value: ObjectType | null) {
      if (!value) return;

      // check if its necesary to cancel an already started fetch.
      this.cancelRequest(idx);

      if (this.objectList[value.model] && this.objectList[value.model].length) {
        // object data already loaded.
        return;
      }

      if (this.loadingObjects[value.model] && this.loadingObjects[value.model].length) {
        // already loading same object type in any row and didnt get the results yet.
        // avoids cancelling this request and calling it again.
        Vue.set(this.loadingObjects[value.model], idx, true);
        return;
      }

      Vue.set(this.loadingObjects[value.model], idx, true);
      this.fetchObjects(idx, value.model)
        .then((data: any) => {
          this.objectList[value.model] = data.relevant_objects;
        })
        .catch(error => {
          if (error.message === CANCEL_MESSAGE) {
            return;
          }
          Vue.set(this.loadingObjects, value.model, []);
          const errorMsg = {
            type: 'error',
            message: 'Failed to load objects',
          };
          if (this.$store) {
            this.$store.dispatch('alerts/show', errorMsg);
          } else {
            window.GGRC.Notify(errorMsg);
          }
        })
        .finally(() => {
          this.cancelFetch.splice(idx, 1);
          Vue.set(this.loadingObjects, value.model, []);
        });
    },
    fetchObjects(idx: number, type: string): Promise<any> {
      const url = buildURL(this.apiUrl, {
        selected: this.selected,
        type: type,
      });

      return getRequest({
        url,
        cancel: cancel => {
          this.setCancelToken(idx, type, cancel);
        },
      });
    },
    setCancelToken(idx, type, cancel) {
      this.cancelFetch[idx] = {
        type,
        cancelFn: cancel,
      };
    },
    objectListOptions(type: ObjectType): ObjectItem[] {
      if (!has(type, 'model')) {
        return [];
      }
      return this.objectList[type.model];
    },
    kebabCase,
  },
});
